import { useTheme } from "next-themes"
import { useEffect, useState } from "react"
import { Toaster as Sonner, toast as sonnerToast } from "sonner"

// ─── Per-variant duration defaults ─────────────────────────────────────────
// We can't pass per-type durations through Sonner's <Toaster> config — its
// `duration` prop is a single global value. To keep this central (no edits
// across 389 call sites), we monkey-patch the singleton `toast` methods at
// module-load so every existing `import { toast } from 'sonner'` picks up
// the calmer defaults automatically. Callers can still override per-call:
//   toast.error('Oh no', { duration: 9000 })
const DURATIONS = {
  success: 2200,
  error: 4500,
  warning: 6000,
  info: 3000,
};

const wrapWithDefaultDuration = (originalFn, defaultDuration) => {
  if (typeof originalFn !== "function") return originalFn;
  return (msg, opts = {}) => originalFn(msg, { duration: defaultDuration, ...opts });
};

if (!sonnerToast.__celeyDurationPatched) {
  sonnerToast.success = wrapWithDefaultDuration(sonnerToast.success, DURATIONS.success);
  sonnerToast.error = wrapWithDefaultDuration(sonnerToast.error, DURATIONS.error);
  sonnerToast.warning = wrapWithDefaultDuration(sonnerToast.warning, DURATIONS.warning);
  sonnerToast.info = wrapWithDefaultDuration(sonnerToast.info, DURATIONS.info);
  sonnerToast.__celeyDurationPatched = true;
}

// Responsive position: mobile devices get top-center to avoid colliding with
// the hamburger menu, notification badge, and role switcher in the top-right
// corner. Desktop keeps the existing top-right placement.
const useResponsiveToastPosition = () => {
  const [position, setPosition] = useState(() =>
    typeof window !== "undefined" && window.matchMedia("(max-width: 639px)").matches
      ? "top-center"
      : "top-right"
  );
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(max-width: 639px)");
    const handler = (e) => setPosition(e.matches ? "top-center" : "top-right");
    if (mq.addEventListener) mq.addEventListener("change", handler);
    else mq.addListener(handler); // legacy Safari
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", handler);
      else mq.removeListener(handler);
    };
  }, []);
  return position;
};

const Toaster = ({
  ...props
}) => {
  const { theme = "system" } = useTheme()
  const position = useResponsiveToastPosition()

  return (
    <Sonner
      theme={theme}
      className="toaster group"
      position={position}
      richColors
      closeButton
      // Fallback duration only — variant-specific durations are applied by
      // the wrapper above. Bare `toast('…')` calls still use this value.
      duration={2500}
      // Critical errors are no longer hidden by a queue of success toasts.
      visibleToasts={3}
      toastOptions={{
        classNames: {
          toast:
            "group toast group-[.toaster]:bg-background group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-lg",
          description: "group-[.toast]:text-muted-foreground",
          actionButton:
            "group-[.toast]:bg-primary group-[.toast]:text-primary-foreground",
          cancelButton:
            "group-[.toast]:bg-muted group-[.toast]:text-muted-foreground",
        },
      }}
      {...props} />
  );
}

export { Toaster, sonnerToast as toast }
